import React, { useMemo } from "react";
import { Col, Divider, Row, Table, Tooltip } from "antd";
import moment from "moment";
import { useAppSelector } from "../../..";
import { checkIsLoading, getDictionaryItemById, toDisplayFormat } from "../../../lib/utils";
import { TableColumn } from "../../../lib/interfaces";
import { ReactComponent as EditIcon } from "../../../img/versions_icons/editVersion_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../../img/versions_icons/addVersion_icon.svg";
import { IChangingMode, IServiceProfile, Offer } from "../../../lib/types";
import { IServiceProfileTableDataRecord } from "../../../containers/OfferContainer/ServiceProfileContainer/utils/types";
import { OFFER_STATUSES } from "../../../store/slices/offering/const";
import { MENU_ADMIN_OFFERING_WRITE } from "../../../lib/const/permissions";
import { hasAccess } from "../../RolesModule/hasAccess";
import "./ServiceProfileView.less";
import { IChangingRecordKeyNumber } from "../../../lib/types/IViewMode";

interface ComponentProps {
  changeViewMode?: (params?: IChangingRecordKeyNumber) => void;
  idx?: number;
  isChild?: boolean;
  serviceProfile?: IServiceProfile;
  isLoading?: boolean;
  isEditingDisabled?: boolean;
  offeringStatusId?: number;
  columns: TableColumn<IServiceProfileTableDataRecord>[];
  offer?: Offer;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ServiceProfileView = (props: ComponentProps) => {
  const {
    columns,
    changeViewMode,
    idx,
    isChild,
    isEditingDisabled,
    isLoading,
    offeringStatusId,
    serviceProfile,
    offer,
  } = props;
  const { billPeriods, billPeriodsLoading } = useAppSelector((state) => state.dicts);
  const {
    paymentType,
    paymentTypeLoading,
    profileTypeStatus: profileTypes,
    profileTypeStatusLoading: profileTypesLoading,
    invoicePeriodStatus: invoicePeriods,
    invoicePeriodStatusLoading: invoicePeriodsLoading,
    prePaymentPeriodStatus: prepaymentPeriods,
    prePaymentPeriodStatusLoading: prepaymentPeriodsLoading,
  } = useAppSelector((state) => state.rtkDictionaries);

  const isDictionariesLoading = useMemo(
    (): boolean =>
      checkIsLoading(
        billPeriodsLoading,
        paymentTypeLoading,
        profileTypesLoading,
        invoicePeriodsLoading,
        prepaymentPeriodsLoading
      ),
    [
      billPeriodsLoading,
      paymentTypeLoading,
      profileTypesLoading,
      invoicePeriodsLoading,
      prepaymentPeriodsLoading,
    ]
  );

  const tableData = useMemo((): IServiceProfileTableDataRecord[] => {
    const fromDate = serviceProfile?.fromDate && toDisplayFormat(serviceProfile.fromDate);
    const toDate = serviceProfile?.toDate && toDisplayFormat(serviceProfile.toDate);
    return [
      {
        id: serviceProfile?.id || 0,
        key: serviceProfile?.id || 0,
        name: serviceProfile?.name,
        profileTypeName: getDictionaryItemById(profileTypes, serviceProfile?.profileTypeId)?.name,
        paymentTypeName: getDictionaryItemById(paymentType, serviceProfile?.paymentTypeId)?.name,
        invoicePeriodName: getDictionaryItemById(invoicePeriods, serviceProfile?.invoicePeriodId)
          ?.name,
        prepaymentPeriodName: getDictionaryItemById(
          prepaymentPeriods,
          serviceProfile?.prepaymentPeriodId
        )?.name,
        billPeriodName: getDictionaryItemById(billPeriods, serviceProfile?.billPeriodId)?.name,
        prepaymentToPayDays: serviceProfile?.prepaymentToPayDays,
        billToPayDays: serviceProfile?.billToPayDays,
        period: [fromDate, toDate]?.filter((el) => Boolean(el)).join(" - "),
      },
    ];
  }, [serviceProfile, isDictionariesLoading]);

  const handleSetViewMode = (
    e: React.MouseEvent<SVGSVGElement, MouseEvent>,
    mode: IChangingMode
  ) => {
    e.stopPropagation();
    changeViewMode && idx !== undefined && changeViewMode([idx, mode]);
  };

  const isOfferPastVersion = useMemo(() => {
    const toDate = offer?.toDate && moment(offer.toDate);
    if (toDate) {
      return toDate.isBefore(moment());
    }
    return false;
  }, [offer]);

  const isPastVersion = useMemo(() => {
    const fromDate = serviceProfile?.fromDate && moment(serviceProfile.fromDate);
    const toDate = serviceProfile?.toDate && moment(serviceProfile.toDate);
    if (fromDate && toDate) {
      return toDate.isBefore(moment());
    }
    return false;
  }, [serviceProfile]);

  const renderButtons = () => {
    const isAvailableOfferingStatus =
      offeringStatusId === OFFER_STATUSES.PREPARE || offeringStatusId === OFFER_STATUSES.SUSPENDED;
    const isButtonsAvailable =
      hasAccess(MENU_ADMIN_OFFERING_WRITE) &&
      isChild &&
      !isEditingDisabled &&
      !isPastVersion &&
      isAvailableOfferingStatus &&
      !isOfferPastVersion;
    return isButtonsAvailable ? (
      <Row gutter={7} justify="end" className="tarification-params-view__btns">
        <Col>
          <Tooltip title="Добавить версию">
            <AddVersionIcon
              onClick={(e) => handleSetViewMode(e, "add")}
              className="tarification-params-view__iconBtn"
            />
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title="Редактировать">
            <EditIcon
              onClick={(e) => handleSetViewMode(e, "edit")}
              className="tarification-params-view__iconBtn"
            />
          </Tooltip>
        </Col>
      </Row>
    ) : null;
  };

  const isCurrentVersion = useMemo(() => {
    const fromDate = serviceProfile?.fromDate && moment(serviceProfile.fromDate);
    const toDate = serviceProfile?.toDate && moment(serviceProfile.toDate);
    if (fromDate && toDate) {
      return moment().isBetween(fromDate, toDate);
    }
    return false;
  }, [serviceProfile]);

  const childClassName = useMemo(() => {
    if (!isChild) return "";
    if (isCurrentVersion) return `row_child row_current-version`;
    if (isPastVersion) return "row_child row_past-version";
    return "row_child";
  }, [isCurrentVersion, isPastVersion, isChild]);

  return (
    <div className={`service-profile-view ${childClassName}`}>
      {isChild && <Divider style={{ margin: "0 0 9px" }} />}
      {renderButtons()}
      <Table
        size="small"
        columns={columns.filter(({ hidden }) => !hidden)}
        dataSource={tableData}
        pagination={false}
        rowClassName={() => childClassName}
        loading={isLoading || isDictionariesLoading}
      />
    </div>
  );
};
